import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';

import { calculatePackagePrice } from 'services/api/pay';
import { find } from 'lodash-es';

import { PackageCheckboxProps, PackageSelectPanel } from './PackageSelectPanel';
import { DurationSelectPanel } from './DurationSelectPanel';

/** 连续包月（自动续费订阅）时长项的 id */
const SUBSCRIPTION_DURATION_ID = 5 as Pay.DurationId;

export interface PackageDetailProps {
  orgId: number;
  packageInfo: Pay.PackageInfoV2;
  currency: General.Currency;
  isEnterprise: boolean;
  setAccountNum: React.Dispatch<React.SetStateAction<number>>;
  setDurationNum: React.Dispatch<React.SetStateAction<Pay.DurationNum | -1>>;
  setCanOrder: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEnterprise: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSubscription?: React.Dispatch<React.SetStateAction<boolean>>;
  onPackageChange?: PackageCheckboxProps['onChange'];
  onCalculatePackageChange?: (pkg?: Pay.CalculatePackagePriceRst) => void;
}

function PackageDetail(props: PackageDetailProps) {
  const {
    packageInfo,
    setAccountNum,
    setDurationNum,
    setCanOrder,
    setIsEnterprise,
    setIsSubscription,
    orgId,
    currency,
    isEnterprise,
    onPackageChange,
    onCalculatePackageChange,
  } = props;
  const { packageData, recommendData } = packageInfo;

  const { recommendPackageId, recommendDurationId } = recommendData;
  // 选中的套餐
  const [chkPackage, setChkPackage] = useState<Pay.PackageId | undefined>(recommendPackageId);
  // 默认选中连续包月（自动续费订阅）
  // 默认选中目录推荐时长（对齐 perseus setCycle：recommendDurationNum 1→连续包月、12→年付，
  // 与推荐角标同源；切区重挂载后按新目录推荐重置）
  const [chkDuration, setChkDuration] = useState<Pay.DurationId>(recommendDurationId);

  // 选中套餐的时长
  const durationDesc = useMemo(() => {
    const checkedPackage = find(packageData, ['id', chkPackage]);
    return checkedPackage?.durationDesc ?? [];
  }, [packageData, chkPackage]);

  const { t } = useTranslate();

  const injectedDurationDesc = useMemo(() => {
    if (!durationDesc.length) return durationDesc;
    const subscriptionItem: Pay.DurationDescV2 = {
      id: SUBSCRIPTION_DURATION_ID,
      name: t('连续包月'),
      durationNum: -1,
      subscription: true,
      price: 0,
      currencyAmount: [],
    };
    // 连续包月放在「一个月」和「一季度」中间（即索引 1）
    return [...durationDesc.slice(0, 1), subscriptionItem, ...durationDesc.slice(1)];
  }, [durationDesc, t]);

  // 报价入参的语义依赖项（标量），目录刷新导致的数组引用变化不触发重报价
  const checkedDurationNum = find(injectedDurationDesc, ['id', chkDuration])?.durationNum;
  const regionPackageId = useMemo(
    () => find(packageData, ['id', chkPackage])?.regionPackageId,
    [packageData, chkPackage],
  );
  const catalogRevision = packageInfo.revision;

  // 重新计算价格（ignore 标记防止过期响应覆盖新报价）
  useEffect(() => {
    if (!chkPackage || checkedDurationNum === undefined) return;
    let ignore = false;
    const durationNum = checkedDurationNum;
    // 目录数据（新价格体系）按 regionPackageId + 目录版本报价；
    // 连续包月报价与下单同参，durationNum 统一传 -1
    calculatePackagePrice(
      regionPackageId && catalogRevision
        ? {
            orgId,
            regionPackageId,
            catalogRevision,
            currency,
            orderType: 'ORDER',
            durationNum,
          }
        : {
            orgId,
            orderType: 'ORDER',
            durationNum,
            newPackageId: chkPackage,
            currency,
          },
    )
      .then((d) => {
        if (ignore) return;
        setDurationNum(durationNum);
        setCanOrder(true);
        onCalculatePackageChange?.(d);
      })
      .catch((e) => {
        if (ignore) return;
        setCanOrder(false);
        onCalculatePackageChange?.(undefined);
        message.error(`/calculatePackagePrice ${e?.message}`);
      });
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chkPackage, checkedDurationNum, currency, regionPackageId, catalogRevision]);

  return (
    <>
      <PackageSelectPanel
        value={chkPackage}
        recommendPackageId={recommendPackageId}
        list={packageData}
        isEnterprise={isEnterprise}
        onChange={(isEnterprise, selected) => {
          if (isEnterprise) {
            // 企业版不支持自动续费
            setIsSubscription?.(false);
            setIsEnterprise(true);
            setChkPackage(undefined);
          } else if (selected) {
            const { accountNum, id } = selected;
            setAccountNum(accountNum);
            setChkPackage(id);
            setChkDuration(SUBSCRIPTION_DURATION_ID);
            setIsSubscription?.(true);
            setIsEnterprise(false);
          }
          onPackageChange?.(isEnterprise, selected);
        }}
      />
      {injectedDurationDesc.length ? (
        <DurationSelectPanel
          durationId={chkDuration}
          packageId={chkPackage}
          currency={currency}
          list={injectedDurationDesc}
          packageData={packageData}
          recommendDurationId={recommendDurationId}
          onChange={({ durationNum, id, subscription }) => {
            setDurationNum(durationNum);
            setChkDuration(id);
            setIsSubscription?.(!!subscription);
          }}
        />
      ) : null}
    </>
  );
}

export default PackageDetail;
